/**
 * @fileoverview Contacts Table Configuration
 *
 * Centralized configuration for contacts and companies tables.
 * Defines columns, sort logic, and picker configurations.
 *
 * @module Contacts/TableConfig
 */

import { TableColumnsConfig, TableSortConfig, PickerConfig } from 'shared/components/UniversalTable/types';
import { ContactTags } from 'shared/types/contacts';

// ========== CONTACTS COLUMN CONFIGURATION ==========

export const CONTACTS_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: [
    {
      id: 'checkbox',
      label: '',
      minWidth: 40,
      defaultWeight: 0,
      className: 'flex justify-center shrink-0'
    },
    {
      id: 'id',
      label: 'ID',
      minWidth: 80,
      defaultWeight: 0,
      sortKey: 'id'
    },
    {
      id: 'firstName',
      label: 'Voornaam',
      minWidth: 120,
      defaultWeight: 2,
      sortKey: 'firstName'
    },
    {
      id: 'lastName',
      label: 'Achternaam',
      minWidth: 120,
      defaultWeight: 2,
      sortKey: 'lastName'
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 200,
      defaultWeight: 3,
      sortKey: 'email'
    },
    {
      id: 'company',
      label: 'Bedrijf',
      minWidth: 150,
      defaultWeight: 2,
      sortKey: 'company'
    },
    {
      id: 'jobTitle',
      label: 'Functie',
      minWidth: 150,
      defaultWeight: 2,
      sortKey: 'jobTitle'
    },
    {
      id: 'phone_mobile',
      label: 'Mobiel',
      minWidth: 120,
      defaultWeight: 1,
    },
    {
      id: 'phone_direct',
      label: 'Telefoon',
      minWidth: 120,
      defaultWeight: 1,
    },
    {
      id: 'buildingLinks',
      label: 'Gebouw / Eenheid',
      minWidth: 200,
      defaultWeight: 3,
    },
    {
      id: 'tags',
      label: 'Tags',
      minWidth: 200,
      defaultWeight: 2,
    },
    {
      id: 'actions',
      label: 'Acties',
      minWidth: 80,
      defaultWeight: 0,
      className: 'flex justify-center shrink-0'
    }
  ],
  localStorageKey: 'contacts-table-column-widths'
};

// ========== COMPANIES COLUMN CONFIGURATION ==========

export const COMPANIES_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: [
    {
      id: 'checkbox',
      label: '',
      minWidth: 40,
      defaultWeight: 0,
      className: 'flex justify-center shrink-0'
    },
    {
      id: 'id',
      label: 'ID',
      minWidth: 80,
      defaultWeight: 0,
      sortKey: 'id'
    },
    {
      id: 'name',
      label: 'Naam',
      minWidth: 200,
      defaultWeight: 3,
      sortKey: 'name'
    },
    {
      id: 'email',
      label: 'Email',
      minWidth: 200,
      defaultWeight: 3,
      sortKey: 'email'
    },
    {
      id: 'phone_direct',
      label: 'Telefoon',
      minWidth: 120,
      defaultWeight: 1,
    },
    {
      id: 'address',
      label: 'Adres',
      minWidth: 200,
      defaultWeight: 2,
    },
    {
      id: 'vatNumber',
      label: 'BTW',
      minWidth: 150,
      defaultWeight: 1,
    },
    {
      id: 'buildingLinks',
      label: 'Gebouw / Eenheid',
      minWidth: 200,
      defaultWeight: 3,
    },
    {
      id: 'tags',
      label: 'Tags',
      minWidth: 200,
      defaultWeight: 2,
    },
    {
      id: 'actions',
      label: 'Acties',
      minWidth: 80,
      defaultWeight: 0,
      className: 'flex justify-center shrink-0'
    }
  ],
  localStorageKey: 'companies-table-column-widths'
};

// ========== CONTACTS SORT CONFIGURATION ==========

export const CONTACTS_SORT_CONFIG: TableSortConfig = {
  defaultSortKey: 'id',
  defaultDirection: 'desc',

  fields: [
    {
      key: 'id',
      extractor: (row) => row.id || 0
    },
    {
      key: 'firstName',
      extractor: (row) => row.firstName?.toLowerCase() || ''
    },
    {
      key: 'lastName',
      extractor: (row) => row.lastName?.toLowerCase() || ''
    },
    {
      key: 'email',
      extractor: (row) => row.email?.toLowerCase() || ''
    },
    {
      key: 'company',
      extractor: (row) => row.company?.name?.toLowerCase() || ''
    },
    {
      key: 'jobTitle',
      extractor: (row) => row.jobTitle?.toLowerCase() || ''
    }
  ],

  caseSensitive: false
};

// ========== COMPANIES SORT CONFIGURATION ==========

export const COMPANIES_SORT_CONFIG: TableSortConfig = {
  defaultSortKey: 'id',
  defaultDirection: 'desc',

  fields: [
    {
      key: 'id',
      extractor: (row) => row.id || 0
    },
    {
      key: 'name',
      extractor: (row) => row.name?.toLowerCase() || ''
    },
    {
      key: 'email',
      extractor: (row) => row.email?.toLowerCase() || ''
    }
  ],

  caseSensitive: false
};

// ========== CONTACT TAGS ==========


// ========== PICKER FIELD CONFIGURATION ==========

export const CONTACTS_PICKER_CONFIG: PickerConfig = {
  entityName: 'contact',
  entityPrefix: '',
  apiUpdateEndpoint: '/contact/update-contact/{id}',
  entityPlural: 'contacten',

  fieldMappings: [
    {
      eventMode: 'tags',
      pickerMode: 'custom-tags' as any,
      fieldName: 'tags',
      titleTemplate: 'Selecteer tags voor {count} {entityName}',
      extractCurrentValue: (row) => row.tags || [],
      buildUpdatePayload: (val) => ({ tags: val }),
      customOptions: Object.values(ContactTags).map(tag => ({ value: tag, label: tag })), // Add customOptions here
      multiSelect: true
    }
  ],

  fieldLabels: {
    tags: 'Tags'
  }
};

export const COMPANIES_PICKER_CONFIG: PickerConfig = {
  entityName: 'bedrijf',
  entityPrefix: '',
  apiUpdateEndpoint: '/contact/update-company/{id}',
  entityPlural: 'bedrijven',

  fieldMappings: [
    {
      eventMode: 'tags',
      pickerMode: 'custom-tags' as any,
      fieldName: 'tags',
      titleTemplate: 'Selecteer tags voor {count} {entityName}',
      extractCurrentValue: (row) => row.tags || [],
      buildUpdatePayload: (val) => ({ tags: val }),
      customOptions: Object.values(ContactTags).map(tag => ({ value: tag, label: tag })), // Add customOptions here
      multiSelect: true
    }
  ],

  fieldLabels: {
    tags: 'Tags'
  }
};

// ========== COMBINED CONFIGURATIONS ==========

export const CONTACTS_TABLE_CONFIG = {
  columnsConfig: CONTACTS_COLUMNS_CONFIG,
  sortConfig: CONTACTS_SORT_CONFIG,
  pickerConfig: CONTACTS_PICKER_CONFIG
};

export const COMPANIES_TABLE_CONFIG = {
  columnsConfig: COMPANIES_COLUMNS_CONFIG,
  sortConfig: COMPANIES_SORT_CONFIG,
  pickerConfig: COMPANIES_PICKER_CONFIG
};
